import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { withClarityTheme } from '@verifime/styles';
import { XpmConnectionIllustration } from '../XpmConnectionIllustration';

type XpmDisconnectConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDisconnecting?: boolean;
};

const XpmDisconnectConfirmModalComponent = ({
  isOpen,
  onClose,
  onConfirm,
  isDisconnecting = false,
}: Readonly<XpmDisconnectConfirmModalProps>) => {
  return (
    <Dialog
      open={isOpen}
      onClose={isDisconnecting ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          maxWidth: '500px',
          width: '100%',
        },
      }}
    >
      <DialogContent
        sx={(theme) => ({
          px: theme.semantic.spacing['2lg'],
          py: theme.semantic.spacing.base,
        })}
      >
        <Stack
          sx={(theme) => ({
            gap: theme.semantic.spacing['2lg'],
          })}
        >
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={(theme) => ({
              gap: theme.semantic.spacing['2xs'],
            })}
          >
            <Typography variant="h6">Disconnect to XPM</Typography>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close"
              disabled={isDisconnecting}
              sx={{ p: 0 }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Connection Status Section */}
          <Stack alignItems="center">
            {/* Integration Illustration */}
            <XpmConnectionIllustration variant="disconnected" />
          </Stack>

          {/* Content Wrapper */}
          <Stack
            alignItems="center"
            sx={(theme) => ({
              gap: theme.semantic.spacing.sm,
              width: '100%',
            })}
          >
            <Typography variant="h4" textAlign="center">
              Are you sure you want to disconnect from XPM integration?
            </Typography>

            <Typography variant="body1" textAlign="center">
              All real-time verifications of your customers from XPM will stop
              working once you disconnect.
            </Typography>
          </Stack>

          {/* Action Buttons */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button variant="outlined" onClick={onClose} disabled={isDisconnecting}>
              CANCEL
            </Button>
            <Button
              variant="contained"
              onClick={onConfirm}
              disabled={isDisconnecting}
              sx={(theme) => ({
                bgcolor: theme.palette.error.main,
                '&:hover': {
                  bgcolor: theme.palette.error.dark,
                },
              })}
            >
              {isDisconnecting ? 'Disconnecting...' : 'YES, DISCONNECT'}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export const XpmDisconnectConfirmModal = withClarityTheme(
  XpmDisconnectConfirmModalComponent,
);
